import {
  clampThresholdSize,
  getThresholdDragBounds,
  ShootTimeBand,
} from "portal/utils/shootTime";
import { getThresholdColor } from "./shared";
import { InputAdornment, useTheme } from "@mui/material";
import { NumberInput } from "common/components/NumberInput";
import { ShootTimeXScale, THRESHOLD_EDIT_INPUT } from "../layout";
import { useTranslation } from "react-i18next";
import React, { FunctionComponent as FC } from "react";

type ThresholdInputsOverlayProps = {
  activeThresholdIndex: number | undefined;
  bands: ShootTimeBand[];
  displaySizes: number[];
  maxRadiusMm: number;
  onActivateThreshold: (index: number) => void;
  onFieldValidityChange: (fieldId: string, isValid: boolean) => void;
  onSizesChange: (sizes: number[]) => void;
  plotTop: number;
  xScale: ShootTimeXScale;
};

/** HTML number inputs positioned over the chart plot. */
export const ThresholdInputsOverlay: FC<ThresholdInputsOverlayProps> = ({
  activeThresholdIndex,
  bands,
  displaySizes,
  maxRadiusMm,
  onActivateThreshold,
  onFieldValidityChange,
  onSizesChange,
  plotTop,
  xScale,
}) => {
  const { t } = useTranslation();
  const {
    palette: {
      background: { paper },
      text: { primary: textPrimary },
    },
  } = useTheme();
  const top =
    plotTop - THRESHOLD_EDIT_INPUT.height - THRESHOLD_EDIT_INPUT.gapAbovePlot;

  return (
    <div className="absolute inset-0 pointer-events-none overflow-visible">
      {displaySizes.map((threshold, index) => {
        const color = getThresholdColor(bands, index);
        const { min, max } = getThresholdDragBounds(
          displaySizes,
          index,
          maxRadiusMm
        );
        const x = xScale(threshold);
        const isActive = index === activeThresholdIndex;
        return (
          <div
            key={bands[index]?.label ?? threshold}
            className="absolute pointer-events-auto -translate-x-1/2"
            style={{
              left: x,
              top,
              width: THRESHOLD_EDIT_INPUT.width,
              zIndex: isActive ? 2 : 1,
            }}
          >
            <NumberInput
              value={threshold}
              min={min}
              max={max}
              onChange={(val) => {
                if (val === undefined) {
                  return;
                }
                onSizesChange(
                  clampThresholdSize(displaySizes, index, val, maxRadiusMm)
                );
              }}
              onValidityChange={(isValid) => {
                onFieldValidityChange(`size-${index}`, isValid);
              }}
              textFieldProps={{
                className:
                  "[&_.MuiInputBase-input]:py-1 [&_.MuiInputBase-input]:px-1.5 [&_.MuiInputBase-input]:text-center [&_.MuiFormHelperText-root]:hidden",
                onFocus: () => {
                  onActivateThreshold(index);
                },
                slotProps: {
                  input: {
                    endAdornment: (
                      <InputAdornment position="end" className="ml-0">
                        <span
                          style={{ color: textPrimary }}
                          className="text-xs"
                        >
                          {t("utils.units.mm")}
                        </span>
                      </InputAdornment>
                    ),
                  },
                  htmlInput: {
                    step: 1,
                    "aria-label": t("utils.units.valueWithUnit", {
                      value: threshold,
                      unit: t("utils.units.mm"),
                    }),
                  },
                },
                sx: {
                  "& .MuiInputBase-root": {
                    backgroundColor: paper,
                    color: textPrimary,
                  },
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: color,
                    borderWidth: 1.5,
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: color,
                  },
                  "& .Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: color,
                  },
                },
              }}
            />
          </div>
        );
      })}
    </div>
  );
};
